import WebSocket from 'ws';
import { promiseFromCallback } from './utils';
import { generateWebSocketOptions } from './utils/network';

export class CDPTestRunner {
    private _lastWsId: number | undefined;
    private _cdpWs: Promise<WebSocket> | null = null;
    private _cdpUrl: string | null = null;
    private _cdpCallbacks = new Map<number, { resolve(value: any): void; reject(e: Error): void }>();

    async initSession(cdpUrl: string, timeout = 500) {
        await this.stopSession();
        this._cdpUrl = cdpUrl;
        await this.initCDPWebsocket(timeout);
    }

    async initCDPWebsocket(timeout = 500) {
        if (this._cdpWs) {
            return this._cdpWs;
        }
        const options = generateWebSocketOptions({ timeout });
        const websocket = new WebSocket(this._cdpUrl!, options);

        const openPromise = promiseFromCallback((cb) => {
            websocket.once('open', cb);
        });

        const errorPromise = promiseFromCallback((cb) => {
            websocket.once('error', cb);
        }).catch(() => {
            websocket.close();
            websocket.removeAllListeners();
        });

        websocket.on('message', (message) => this.onCDPMessage(message));

        this._cdpWs = Promise.race([openPromise, errorPromise]).then(() => websocket);
        return this._cdpWs;
    }

    onCDPMessage(message: WebSocket.RawData) {
        // @ts-expect-error JSON.parse handles buffers as well, the types are not smart enough :(
        const object = JSON.parse(message);
        const callback = this._cdpCallbacks.get(object.id);
        if (!callback) {
            return;
        }
        this._cdpCallbacks.delete(object.id);
        if (object.error) {
            callback.reject(new Error(object.error));
        } else if (object.result.exceptionDetails?.exception) {
            callback.reject(new Error(object.result.exceptionDetails.exception.description));
        } else {
            callback.resolve(object.result);
        }
    }

    async stopSession() {
        const websocket = this._cdpWs;
        this._cdpUrl = null;
        this._cdpWs = null;
        this._cdpCallbacks.clear();
        if (websocket) {
            try {
                return (await websocket).close();
            } catch {
                return undefined;
            }
        }
        return undefined;
    }

    async cdpCommand<T = any>(method: string, params?: object, sessionId?: string) {
        const websocket = await this.initCDPWebsocket();
        this._lastWsId ||= 0;
        const id = this._lastWsId++;
        const result = new Promise<T>((resolve, reject) => {
            this._cdpCallbacks.set(id, { resolve, reject });
        });
        const msg = { method, params, id };
        if (sessionId) {
            Object.assign(msg, { sessionId });
        }
        websocket.send(JSON.stringify(msg));
        return result;
    }
}
